import React, { useState, useEffect } from 'react';
import { Form2Data } from '../types';

interface Step3Props {
    onSubmit: (data: Partial<Form2Data>) => void;
    onBack: () => void;
    initialData: Partial<Form2Data>;
    isSubmitting: boolean;
    submissionError: string | null;
}

const Step3Consent: React.FC<Step3Props> = ({ onSubmit, onBack, initialData, isSubmitting, submissionError }) => {
    const [formData, setFormData] = useState<Partial<Form2Data>>(initialData);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
        if (error) {
            setError(null);
        }
    };

    const handleSubmit = () => {
        if (formData.zustimmungDatenschutz) {
            onSubmit(formData);
        } else {
            setError('Bitte stimmen Sie den Datenschutzbestimmungen zu, um fortzufahren.');
        }
    };

    const allChecked = formData.zustimmungDatenschutz;

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-custom mb-6 text-center">Einverständnis</h1>
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="space-y-6">
                    <div className="relative flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                id="einverstaendnisDaten"
                                name="einverstaendnisDaten"
                                type="checkbox"
                                checked={!!formData.einverstaendnisDaten}
                                onChange={handleChange}
                                className="focus:ring-magenta h-4 w-4 text-magenta border-gray-300 rounded"
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="einverstaendnisDaten" className="font-medium text-gray-700">
                                Ich erkläre mich damit einverstanden, dass über die Veranstaltungsapp meine beruflichen Daten mit Kolleg*innen geteilt werden. <span className="text-gray-500">(optional)</span>
                            </label>
                        </div>
                    </div>
                    <div className="relative flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                id="einverstaendnisFotos"
                                name="einverstaendnisFotos"
                                type="checkbox"
                                checked={!!formData.einverstaendnisFotos}
                                onChange={handleChange}
                                className="focus:ring-magenta h-4 w-4 text-magenta border-gray-300 rounded"
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="einverstaendnisFotos" className="font-medium text-gray-700">
                                Ich erkläre mich damit einverstanden, dass während der Veranstaltung Fotoaufnahmen gemacht werden, welche im Nachhinein zu internen und externen Zwecken genutzt werden. <span className="text-gray-500">(optional)</span>
                            </label>
                        </div>
                    </div>
                    <div className="relative flex items-start">
                        <div className="flex items-center h-5">
                            <input
                                id="zustimmungDatenschutz"
                                name="zustimmungDatenschutz"
                                type="checkbox"
                                checked={!!formData.zustimmungDatenschutz}
                                onChange={handleChange}
                                className="focus:ring-magenta h-4 w-4 text-magenta border-gray-300 rounded"
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="zustimmungDatenschutz" className="font-medium text-gray-700">
                                Ich stimme den Datenschutzbestimmungen zu.
                            </label>
                        </div>
                    </div>
                </div>

                {error && <p className="mt-4 text-sm text-red-600 text-center">{error}</p>}
                {submissionError && <p className="mt-4 text-sm text-red-600 text-center">{submissionError}</p>}

                <div className="mt-8 border-t pt-6 flex flex-col md:flex-row-reverse gap-4">
                    <button type="button" onClick={handleSubmit} disabled={!allChecked || isSubmitting} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-magenta hover:bg-berry focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-magenta disabled:opacity-50">
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Wird gesendet...</span>
                            </>
                        ) : (
                            'Absenden'
                        )}
                    </button>
                    <button type="button" onClick={onBack} disabled={isSubmitting} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-custom disabled:opacity-50">
                        Zurück
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Step3Consent;
